import {
  useMemo,
  useCallback,
  useState,
  useEffect,
  type ComponentType,
  type ReactNode,
} from 'react'
import { Button, Input, Progress, Typography } from 'antd'
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable'
import type { DragEndEvent } from '@dnd-kit/core'
import { Plus } from 'lucide-react'
import { v4 as uuidv4 } from 'uuid'
import {
  useBatchQuestionState,
  type BatchQuestion,
  type NodeMetadataEntry,
} from '../hooks/useBatchQuestionState'
import { QuestionEditor } from '../../survey/components/builder'
import { InterviewQuestionEditor } from '../../interview/components/builder'
import type { SurveyQuestion, InterviewQuestion } from '@bandos/shared'
import { ProfileService } from '../../../services/profile/profileService'
import { CoveragePanel } from './CoveragePanel'
import { QuestionAttributionNote } from './QuestionAttributionNote'
import {
  ReviewContainer,
  ReviewHeader,
  ReviewTitle,
  ReviewSubtitle,
  NameFields,
  ScrollBody,
  ReviewFooter,
  Summary,
  QuotaRow,
  QuotaFooterBar,
  BottomBar,
  AddButton,
} from './BatchQuestionReview.styles'

const { Text } = Typography

// ─── Public types ───────────────────────────────────────────────────────────

export type ReviewedBatch =
  | { method: 'survey'; name: string; description: string; questions: SurveyQuestion[] }
  | { method: 'interview'; name: string; description: string; questions: InterviewQuestion[] }

interface BatchQuestionReviewProps {
  /** Flat list of questions from the holistic generator (each has `sourceNodeIds`). */
  questions: BatchQuestion[]
  /** Per-node label + category for the coverage panel and attribution chips. */
  nodeMetadata: Record<string, { label: string; category: string }>
  method: 'survey' | 'interview'
  onBack: () => void
  onCreate: (result: ReviewedBatch) => void
}

// Editor contract: both QuestionEditor and InterviewQuestionEditor satisfy this.
export interface BatchQuestionEditorProps<Q extends BatchQuestion> {
  id: string
  question: Q
  index: number
  onChange: (q: Q) => void
  onDelete: () => void
  attribution?: ReactNode
}

// ─── Defaults ───────────────────────────────────────────────────────────────

const DEFAULT_SURVEY_QUESTION: SurveyQuestion = { question: '', type: 'open' }
const DEFAULT_INTERVIEW_QUESTION: InterviewQuestion = { question: '' }

function toMetadataEntries(
  nodeMetadata: Record<string, { label: string; category: string }>
): Record<string, NodeMetadataEntry> {
  const result: Record<string, NodeMetadataEntry> = {}
  for (const [id, m] of Object.entries(nodeMetadata)) {
    result[id] = { nodeLabel: m.label, nodeCategory: m.category }
  }
  return result
}

// ─── Dispatcher ─────────────────────────────────────────────────────────────

export function BatchQuestionReview({
  questions,
  nodeMetadata,
  method,
  onBack,
  onCreate,
}: BatchQuestionReviewProps) {
  if (method === 'survey') {
    return (
      <BatchReviewBody<SurveyQuestion>
        questions={questions as SurveyQuestion[]}
        nodeMetadata={nodeMetadata}
        Editor={QuestionEditor}
        defaultQuestion={DEFAULT_SURVEY_QUESTION}
        ctaLabel="Create Survey"
        method="survey"
        methodLabel="survey"
        namePlaceholder="e.g. Round 1 Customer Survey"
        onBack={onBack}
        onCreate={(name, description, qs) =>
          onCreate({ method: 'survey', name, description, questions: qs })
        }
      />
    )
  }
  return (
    <BatchReviewBody<InterviewQuestion>
      questions={questions as InterviewQuestion[]}
      nodeMetadata={nodeMetadata}
      Editor={InterviewQuestionEditor}
      defaultQuestion={DEFAULT_INTERVIEW_QUESTION}
      ctaLabel="Create Interview Guide"
      method="interview"
      methodLabel="interview guide"
      namePlaceholder="e.g. Founder Discovery Interview"
      onBack={onBack}
      onCreate={(name, description, qs) =>
        onCreate({ method: 'interview', name, description, questions: qs })
      }
    />
  )
}

// ─── Generic body ───────────────────────────────────────────────────────────

interface BatchReviewBodyProps<Q extends BatchQuestion> {
  questions: Q[]
  nodeMetadata: Record<string, { label: string; category: string }>
  Editor: ComponentType<BatchQuestionEditorProps<Q>>
  defaultQuestion: Q
  ctaLabel: string
  method: 'survey' | 'interview'
  methodLabel: string
  defaultName?: string
  namePlaceholder: string
  onBack: () => void
  onCreate: (name: string, description: string, questions: Q[]) => void
}

function BatchReviewBody<Q extends BatchQuestion>({
  questions: initialQuestions,
  nodeMetadata,
  Editor,
  defaultQuestion,
  ctaLabel,
  method,
  methodLabel,
  defaultName,
  namePlaceholder,
  onBack,
  onCreate,
}: BatchReviewBodyProps<Q>) {
  const [name, setName] = useState(defaultName ?? '')
  const [description, setDescription] = useState('')
  const [quota, setQuota] = useState<{
    currentCount: number
    monthlyLimit: number
    canCreate: boolean
    isUnlimited: boolean
  } | null>(null)

  useEffect(() => {
    const fetchQuota = async () => {
      const res =
        method === 'survey'
          ? await ProfileService.getSurveyQuota()
          : await ProfileService.getInterviewQuota()
      if (!res.error && res.data) {
        setQuota(res.data)
      }
    }
    void fetchQuota()
  }, [method])

  const metadataByNode = useMemo(() => toMetadataEntries(nodeMetadata), [nodeMetadata])

  const { questions, questionIds, updateQuestion, removeQuestion, addQuestion, reorderQuestions } =
    useBatchQuestionState<Q>(initialQuestions, metadataByNode)

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }))

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (!over || active.id === over.id) return
      const oldIndex = questionIds.indexOf(active.id as string)
      const newIndex = questionIds.indexOf(over.id as string)
      if (oldIndex < 0 || newIndex < 0) return
      reorderQuestions(
        arrayMove(questions, oldIndex, newIndex),
        arrayMove(questionIds, oldIndex, newIndex)
      )
    },
    [questions, questionIds, reorderQuestions]
  )

  const handleAdd = useCallback(() => {
    addQuestion({ ...defaultQuestion, id: uuidv4() } as Q)
  }, [addQuestion, defaultQuestion])

  const handleAttributionChange = useCallback(
    (
      index: number,
      next: {
        sourceNodeIds: string[]
        sourceNodeLabels: string[]
        sourceNodeCategories: string[]
      }
    ) => {
      // Set the new array fields and explicitly clear the deprecated singletons
      // so the persisted shape has exactly one source of truth. `undefined`
      // fields are dropped by JSON.stringify before the network call.
      const updated: Q = {
        ...questions[index],
        sourceNodeIds: next.sourceNodeIds,
        sourceNodeLabels: next.sourceNodeLabels,
        sourceNodeCategories: next.sourceNodeCategories,
        sourceNodeId: undefined,
        sourceNodeLabel: undefined,
        sourceNodeCategory: undefined,
      }
      updateQuestion(index, updated)
    },
    [questions, updateQuestion]
  )

  const totalQuestions = questions.length

  const handleCreate = useCallback(() => {
    onCreate(
      name.trim(),
      description.trim(),
      questions.map((q) => ({ ...q, id: q.id ?? uuidv4() }))
    )
  }, [questions, name, description, onCreate])

  return (
    <ReviewContainer>
      <ScrollBody>
        <ReviewHeader>
          <ReviewTitle>Review questions</ReviewTitle>
          <ReviewSubtitle>
            Edit, reorder, add, or delete questions before creating your {methodLabel}. Attribution
            chips show which nodes each question covers — click a chip to change it.
          </ReviewSubtitle>
          <NameFields>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={namePlaceholder}
              aria-label="Name"
            />
            <Input.TextArea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional — add a brief description"
              autoSize={{ minRows: 2, maxRows: 4 }}
              aria-label="Description"
            />
          </NameFields>
        </ReviewHeader>

        <CoveragePanel questions={questions} metadataByNode={metadataByNode} />

        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <SortableContext items={questionIds} strategy={verticalListSortingStrategy}>
            {questions.map((q, i) => (
              <Editor
                key={questionIds[i]}
                id={questionIds[i]}
                question={q}
                index={i}
                onChange={(updated) => updateQuestion(i, updated)}
                onDelete={() => removeQuestion(i)}
                attribution={
                  <QuestionAttributionNote
                    question={q}
                    metadataByNode={metadataByNode}
                    onChange={(next) => handleAttributionChange(i, next)}
                  />
                }
              />
            ))}
          </SortableContext>
        </DndContext>

        <AddButton type="button" onClick={handleAdd}>
          <Plus size={12} />
          Add question
        </AddButton>
      </ScrollBody>

      <BottomBar>
        {quota && !quota.isUnlimited && (
          <QuotaFooterBar>
            <QuotaRow>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {method === 'survey' ? 'Surveys' : 'Interviews'} this month: {quota.currentCount} /{' '}
                {quota.monthlyLimit}
              </Text>
              {!quota.canCreate && (
                <Text type="danger" style={{ fontSize: 12 }}>
                  Monthly limit reached
                </Text>
              )}
            </QuotaRow>
            <Progress
              percent={Math.min(100, Math.round((quota.currentCount / quota.monthlyLimit) * 100))}
              size="small"
              status={
                quota.currentCount >= quota.monthlyLimit
                  ? 'exception'
                  : quota.currentCount / quota.monthlyLimit >= 0.8
                    ? 'active'
                    : 'normal'
              }
              showInfo={false}
            />
          </QuotaFooterBar>
        )}

        <ReviewFooter>
          <Summary>
            {totalQuestions} question{totalQuestions !== 1 ? 's' : ''} total
          </Summary>
          <Button type="text" onClick={onBack}>
            Back
          </Button>
          <Button
            type="primary"
            onClick={handleCreate}
            disabled={totalQuestions === 0 || quota?.canCreate === false}
          >
            {ctaLabel}
          </Button>
        </ReviewFooter>
      </BottomBar>
    </ReviewContainer>
  )
}
